import { z } from 'zod'

import { IntegrationShortSchema } from 'shared/api/common/schemas/integration'

import { InstalledApplicationSchema } from './applications'
import { EmptyArraySchema } from './base'
import { defaultReplySchema } from './flow/defaultReply'
import { WelcomeMessageSchema } from './flow/welcomeMessage'
import { channelData as whatsAppChannelData } from './whatsAppChannelData'

export const ExtendedDataSchema = z.object({
  data: z.object({
    page_id: z.string(),
    id: z.string(),
    title: z.string(),
    ava_small: z.string().nullable(),
    ava_big: z.string().nullable(),
    default_avatar_color: z.string(),
    threads_statuses: z.object({
      new: z.number(),
      opened: z.number(),
      closed: z.number(),
    }),
    pro_status: z.string(),
    is_grandfathering: z.boolean(),
    billing_version: z.number(),
    // Empty array or object
    billing_next_action_required: z.array(z.never()).or(
      z.object({
        next_action_redirect: z.string().url(),
        action_type: z.string(),
        amount: z.number(),
        card_last4: z.string(),
      }),
    ),
    grandfathering_version: z.number().nullable(),
    is_trial_available: z.boolean(),
    is_blocked: z.boolean(),
    block_after_expire_ts: z.number().nullable(),
    enable_zapier: z.boolean(),
    last_devlog_critical_message_ts: z.number().nullable(),
    interface_data: z
      .object({
        display_flags: z.record(z.string(), z.boolean()).optional(),
        display_preferences: z.record(z.string(), z.record(z.string(), z.boolean())).optional(),
      })
      .nullable(),
    flags: z.record(z.string(), z.boolean()),
    timezone: z.string(),
    public_api_access_token: z.string().nullable(),
    connected_integrations: IntegrationShortSchema.or(EmptyArraySchema),
    enable_ref_url_preview: z.boolean(),
    fb_channel: z.object({
      fb_page_id: z.string().nullable(),
      fb_page_name: z.string(),
      page_link: z.string(),
      has_subscription_permission: z.boolean(),
      has_pages_messaging_perm_processing_error: z.boolean(),
      about: z.string().nullable(),
      is_fb_channel_disabled: z.boolean(),
      rn_broadcast_onboarding_banner_active: z.boolean(),
      should_rn_flow_creation_show: z.boolean(),
      status: z.string(),
      description: z.string(),
      link: z.string().nullable(),
      page_username: z.string(),
      enable_fb_channel: z.boolean(),
      icebreakers_enabled: z.boolean(),
      token_without_admin_perm: z.boolean(),
      eu_affect: z.object({ degree: z.string() }).nullable(),
      default_reply: defaultReplySchema.optional(),
      welcome_message: WelcomeMessageSchema.optional(),
      ts_channel_connected: z.string().nullish(),
    }),
    instagram_channel: z.object({
      enable_instagram_node: z.boolean(),
      ig_rn_broadcast_onboarding_banner_active: z.boolean(),
      is_ig_page_unlinked: z.boolean(),
      status: z.string(),
      is_channel_connected: z.boolean(),
      is_channel_enabled: z.boolean(),
      ts_channel_connected: z.string().nullish(),
      business_id: z.string().nullable(),
      username: z.string().nullable(),
      avatar: z.string().nullable(),
      default_reply: defaultReplySchema.optional(),
      is_new_ig_api: z.boolean(),
      is_ig_unified_onboarding_enabled: z.boolean(),
      icebreakers_instagram_enabled: z.boolean(),
      can_use_ig_auto_reply: z.boolean(),
      is_ig_summer_promo_available: z.boolean(),
      is_ig_summer_promo_activated: z.boolean(),
      show_ig_summit_ticket_launch_banner: z.boolean(),
    }),
    sms_channel: z.object({
      enable_sms_node: z.boolean(),
      is_blocked: z.boolean(),
      status: z.string(),
      sms_channel_connected: z.boolean(),
      sms_has_phones: z.boolean(),
      ts_channel_connected: z.string().nullish(),
    }),
    email_channel: z.object({
      enable_email_node: z.boolean(),
      status: z.string(),
      email_channel_banned: z.boolean(),
      ts_channel_connected: z.string().nullish(),
    }),
    telegram_channel: z.object({
      is_channel_connected: z.boolean(),
      is_channel_enabled: z.boolean(),
      ts_channel_connected: z.string().nullish(),
      status: z.string(),
      bot_name: z.string().nullable(),
      bot_username: z.string().nullable(),
      default_reply: defaultReplySchema.optional(),
      welcome_message: WelcomeMessageSchema.optional(),
    }),
    ai_intent_status: z.string(),
    ts_added: z.number(),
    csrf_token: z.string(),
    widget_branding_enabled: z.boolean(),
    billing_type: z.string(),
    can_import_telegram_subscribers: z.boolean(),
    can_import_whatsapp_subscribers: z.boolean(),
    can_import_sms_subscribers: z.boolean(),
    can_import_email_subscribers: z.boolean(),
    has_any_broadcasts: z.boolean(),
    can_subscribers_bulk_deletion: z.boolean(),
    ai_intent_recommendations: z.array(z.unknown()),
    notify: z.object({
      server: z.string(),
      user: z.string(),
      timestamp: z.string(),
      token: z.string(),
      channel: z.string(),
    }),
    payments_enabled: z.boolean(),
    payments_settings_enabled: z.boolean(),
    is_buy_button_enabled: z.boolean(),
    is_lc_product_catalog_enabled: z.boolean(),
    can_apply_revenue_sharing: z.boolean(),
    is_integration_settings_allowed: z.boolean().optional(),
    allowed_integrations: z.array(z.string()).optional(),
    allow_free_branding: z.boolean(),
    whatsapp_channel: whatsAppChannelData,
    installed_applications: z.array(InstalledApplicationSchema).optional(),
    block_limits: z.object({
      keywords: z.number(),
      contacts: z.number(),
      users: z.number(),
    }),
    block_reasons: z.array(z.unknown()),
    products: z.object({
      is_grandfathering: z.boolean(),
      has_inbox: z.boolean(),
      has_automation: z.boolean(),
    }),
    self_serf: z.object({
      priority_channel: z.string().nullable(),
      use_case_id: z.string().nullable(),
      industry: z.string(),
    }),
    ig_onboarding_show_trigger: z.boolean(),
    category: z.string().optional(),
  }),
  state: z.boolean().optional(),
})
